/**
 * Auth hooks abstraction layer
 * Updated for Better Auth (migrated from Better Auth)
 *
 * Uses better-auth/client (vanilla JS) instead of better-auth/react
 * to avoid React duplicate instance issues in Electron/monorepo setups.
 * We create our own React hooks that wrap the vanilla client.
 */

import { useMemo, useCallback, useState, useEffect, useSyncExternalStore } from 'react';
import { createAuthClient } from 'better-auth/client';

// Create the Better Auth client
// The baseURL should point to your API server
const BASE_URL = typeof window !== 'undefined'
  ? (import.meta.env?.VITE_API_URL || window.ENV?.API_URL || 'http://localhost:5000')
  : 'http://localhost:5000';

// Remove trailing /api if present (we'll add /api/auth)
const API_BASE = BASE_URL.replace(/\/api\/?$/, '');

// Create auth client - baseURL must include the full auth path
const authClient = createAuthClient({
  baseURL: `${API_BASE}/api/auth`,
  fetchOptions: {
    credentials: 'include' // Include cookies for session management
  }
});

// Export the raw client for advanced use cases
export { authClient };

// Session cache for sharing state across hooks
let sessionCache = { data: null, error: null, isLoading: true };
let sessionListeners = new Set();
let sessionFetchPromise = null;

function notifyListeners() {
  sessionListeners.forEach(listener => listener({ ...sessionCache }));
}

async function fetchSession() {
  // If already fetching, return the existing promise
  if (sessionFetchPromise) return sessionFetchPromise;

  sessionFetchPromise = (async () => {
    try {
      // Check for bearer token in localStorage (for cross-origin auth)
      const bearerToken = typeof window !== 'undefined'
        ? localStorage.getItem('better_auth_token')
        : null;

      // Use bearer token if available, otherwise rely on cookies
      const fetchOptions = bearerToken
        ? { headers: { Authorization: `Bearer ${bearerToken}` } }
        : {};

      // Add timeout to prevent infinite loading
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Session fetch timeout')), 10000);
      });

      const result = await Promise.race([
        authClient.getSession({ fetchOptions }),
        timeoutPromise
      ]);

      sessionCache = {
        data: result.data,
        error: result.error || null,
        isLoading: false
      };
    } catch (err) {
      console.warn('[Auth] Session fetch failed:', err.message);
      sessionCache = { data: null, error: err, isLoading: false };
    }
    sessionFetchPromise = null;
    notifyListeners();
    return sessionCache;
  })();

  return sessionFetchPromise;
}

// Refresh session (call after sign-in/sign-out)
export function refreshSession() {
  sessionCache = { ...sessionCache, isLoading: true };
  notifyListeners();
  return fetchSession();
}

// Subscribe function for useSyncExternalStore
function subscribeToSession(callback) {
  sessionListeners.add(callback);

  // Trigger initial fetch if needed
  if (sessionCache.isLoading && !sessionFetchPromise) {
    fetchSession();
  }

  return () => {
    sessionListeners.delete(callback);
  };
}

// Snapshot function for useSyncExternalStore
function getSessionSnapshot() {
  return sessionCache;
}

/**
 * Custom hook to get session state
 * Uses useSyncExternalStore for synchronous state updates
 * This ensures auth state is immediately available after sign-in/sign-up
 */
function useSession() {
  const state = useSyncExternalStore(subscribeToSession, getSessionSnapshot, getSessionSnapshot);

  return {
    data: state.data,
    error: state.error,
    isPending: state.isLoading
  };
}

/**
 * Get the current authenticated user in a normalized format
 * @returns {{ user: import('./types').AuthUser | null, isLoaded: boolean }}
 */
export function useCurrentUser() {
  const { data: session, isPending, error } = useSession();

  // Memoize to prevent infinite re-renders when used in useCallback dependencies
  const normalizedUser = useMemo(() => {
    if (isPending || !session?.user) return null;
    return {
      id: session.user.id,
      email: session.user.email || '',
      firstName: session.user.name?.split(' ')[0] || null,
      lastName: session.user.name?.split(' ').slice(1).join(' ') || null,
      imageUrl: session.user.image || null,
    };
  }, [isPending, session?.user?.id, session?.user?.email, session?.user?.name, session?.user?.image]);

  return { user: normalizedUser, isLoaded: !isPending, error };
}

/**
 * Get auth state (signed in status)
 * @returns {import('./types').AuthState}
 */
export function useAuthState() {
  const { data: session, isPending } = useSession();
  return {
    isSignedIn: !!session?.user,
    isLoaded: !isPending
  };
}

/**
 * Get token management functions
 * @returns {import('./types').AuthToken}
 */
export function useAuthToken() {
  const { data: session, isPending } = useSession();

  const getToken = useCallback(async () => {
    // With Better Auth + Bearer plugin, the token is stored after sign-in
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('better_auth_token');
      if (token) return token;
    }

    // If no stored token but we have a session, we're using cookie-based auth
    // Return null to indicate cookie-based auth is in use
    if (session?.user) {
      return null; // Cookie-based auth
    }

    return null;
  }, [session?.user]);

  return { getToken, isLoaded: !isPending };
}

/**
 * Get sign-in flow methods
 * @returns {import('./types').SignInFlow}
 */
export function useSignInFlow() {
  const [isLoaded, setIsLoaded] = useState(true);
  const [error, setError] = useState(null);

  const createSignIn = useCallback(async ({ identifier, password }) => {
    setError(null);
    try {
      // Don't send callbackURL for Electron (file:// protocol)
      const isElectron = window.location.protocol === 'file:';
      const callbackURL = isElectron ? undefined : window.location.origin + '/dashboard';

      // Variable to capture bearer token from response headers
      let bearerToken = null;

      const result = await authClient.signIn.email({
        email: identifier,
        password,
        ...(callbackURL && { callbackURL })
      }, {
        onSuccess: (ctx) => {
          // Better Auth bearer plugin returns token in response headers
          const headerToken = ctx.response?.headers?.get('set-auth-token');
          if (headerToken) {
            bearerToken = headerToken;
            console.log('[Auth] Bearer token captured from response headers');
          }
        },
        onError: (ctx) => {
          setError(ctx.error);
        }
      });

      // Debug: Log the result to see what Better Auth returns
      console.log('[Auth] Sign-in result:', JSON.stringify(result, null, 2));

      // Check if Better Auth returned an error in the result
      if (result?.error) {
        console.log('[Auth] Error found in result:', result.error);
        const err = new Error(result.error.message || 'Invalid email or password');
        setError(err);
        throw err;
      }

      // Also check if data is missing (another sign of failure)
      if (!result?.data) {
        console.log('[Auth] No data in result - treating as error');
        const err = new Error('Invalid email or password');
        setError(err);
        throw err;
      }

      // Store Bearer token if captured from headers (preferred for Electron)
      if (bearerToken) {
        localStorage.setItem('better_auth_token', bearerToken);
        console.log('[Auth] Bearer token stored after sign-in (from headers)');
      } else if (result?.data?.token) {
        // Fallback: check if token is in response body
        localStorage.setItem('better_auth_token', result.data.token);
        console.log('[Auth] Bearer token stored after sign-in (from body)');
      }

      // Refresh session cache AFTER sign-in completes
      if (result?.data) {
        await refreshSession();
      }

      return result;
    } catch (err) {
      setError(err);
      throw err;
    }
  }, []);

  // setActive is not needed with Better Auth (session is automatic)
  const setActive = useCallback(async () => {
    // No-op for Better Auth - session is automatically active after sign-in
    return Promise.resolve();
  }, []);

  return {
    signIn: authClient.signIn,
    isLoaded,
    error,
    createSignIn,
    setActive
  };
}

/**
 * Get sign-up flow methods
 * @returns {import('./types').SignUpFlow}
 */
export function useSignUpFlow() {
  const [isLoaded, setIsLoaded] = useState(true);
  const [error, setError] = useState(null);
  const [pendingVerification, setPendingVerification] = useState(false);

  const createSignUp = useCallback(async ({ emailAddress, password, firstName, lastName, role = 'owner' }) => {
    setError(null);
    try {
      const name = [firstName, lastName].filter(Boolean).join(' ');

      // Don't send callbackURL for Electron (file:// protocol)
      const isElectron = window.location.protocol === 'file:';
      const callbackURL = isElectron ? undefined : window.location.origin + '/dashboard';

      // Variable to capture bearer token from response headers
      let bearerToken = null;

      const result = await authClient.signUp.email({
        email: emailAddress,
        password,
        name,
        role, // Pass role to Better Auth (defaults to 'owner' for business portal, 'student' for customer portal)
        ...(callbackURL && { callbackURL })
      }, {
        onSuccess: (ctx) => {
          // Better Auth bearer plugin returns token in response headers
          const headerToken = ctx.response?.headers?.get('set-auth-token');
          if (headerToken) {
            bearerToken = headerToken;
            console.log('[Auth] Bearer token captured from response headers');
          }
        },
        onError: (ctx) => {
          setError(ctx.error);
        }
      });

      // Check if Better Auth returned an error in the result
      if (result?.error) {
        const err = new Error(result.error.message || 'Sign up failed');
        setError(err);
        throw err;
      }

      // Store Bearer token if captured from headers (preferred for Electron)
      if (bearerToken) {
        localStorage.setItem('better_auth_token', bearerToken);
        console.log('[Auth] Bearer token stored after sign-up (from headers)');
      } else if (result?.data?.token) {
        // Fallback: check if token is in response body
        localStorage.setItem('better_auth_token', result.data.token);
        console.log('[Auth] Bearer token stored after sign-up (from body)');
      }

      // Refresh session cache AFTER sign-up completes
      if (result?.data) {
        await refreshSession();
      }

      return result;
    } catch (err) {
      setError(err);
      throw err;
    }
  }, []);

  // Email verification methods (if email verification is enabled)
  const prepareEmailVerification = useCallback(async () => {
    try {
      await authClient.sendVerificationEmail({
        callbackURL: window.location.origin + '/verify-email'
      });
      setPendingVerification(true);
    } catch (err) {
      setError(err);
      throw err;
    }
  }, []);

  const attemptEmailVerification = useCallback(async ({ code }) => {
    // Better Auth handles verification via URL callback, not code
    // This is kept for API compatibility
    console.log('[Auth] Email verification is handled via callback URL in Better Auth');
    return { status: 'complete' };
  }, []);

  // setActive is not needed with Better Auth
  const setActive = useCallback(async () => {
    return Promise.resolve();
  }, []);

  return {
    signUp: authClient.signUp,
    isLoaded,
    error,
    pendingVerification,
    createSignUp,
    prepareEmailVerification,
    attemptEmailVerification,
    setActive
  };
}

/**
 * Get sign-out function
 * @returns {{ signOut: () => Promise<void> }}
 */
export function useSignOut() {
  const signOut = useCallback(async () => {
    // Clear stored token
    if (typeof window !== 'undefined') {
      localStorage.removeItem('better_auth_token');
    }

    // Clear session cache immediately
    sessionCache = { data: null, error: null, isLoading: false };
    notifyListeners();

    // Helper to redirect after sign-out
    const redirectToLogin = () => {
      const isElectron = window.location.protocol === 'file:';
      if (isElectron) {
        window.location.hash = '#/login';
      } else {
        window.location.href = '/';
      }
    };

    try {
      await authClient.signOut({
        fetchOptions: {
          onSuccess: redirectToLogin
        }
      });
    } catch (error) {
      // Even if sign-out API fails (e.g., session already expired),
      // we've already cleared local state, so redirect anyway
      console.warn('[Auth] signOut API error (redirecting anyway):', error);
      redirectToLogin();
    }
  }, []);

  return { signOut };
}

/**
 * Get password reset flow methods
 * @returns {import('./types').PasswordResetFlow}
 */
export function usePasswordReset() {
  const [isLoaded, setIsLoaded] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Start password reset flow by sending a reset email
   * @param {string} email - The user's email address
   */
  const sendResetCode = useCallback(async (email) => {
    setError(null);
    try {
      // Better Auth uses requestPasswordReset (not forgotPassword)
      await authClient.requestPasswordReset({
        email,
        redirectTo: window.location.origin + '/forgot-password'
      });
      return { status: 'needs_new_password' };
    } catch (err) {
      setError(err);
      throw err;
    }
  }, []);

  /**
   * Verify the reset code (not used in Better Auth - verification is via URL)
   */
  const verifyResetCode = useCallback(async (code) => {
    // Better Auth uses URL-based verification, not code-based
    console.log('[Auth] Password reset verification is handled via callback URL');
    return { status: 'needs_new_password' };
  }, []);

  /**
   * Set the new password after verification
   * @param {string} password - The new password
   * @param {string} token - The reset token from the URL
   */
  const resetPassword = useCallback(async (password, token) => {
    setError(null);
    try {
      const result = await authClient.resetPassword({
        newPassword: password,
        token // Token comes from the reset URL
      });

      // Store Bearer token if returned (similar to signIn/signUp)
      if (result?.data?.token) {
        localStorage.setItem('better_auth_token', result.data.token);
        console.log('[Auth] Bearer token stored after password reset');
      }

      // Refresh session cache to pick up the new session
      await refreshSession();

      return { status: 'complete', data: result?.data };
    } catch (err) {
      setError(err);
      throw err;
    }
  }, []);

  // setActive is not needed with Better Auth
  const setActive = useCallback(async () => {
    return Promise.resolve();
  }, []);

  return {
    isLoaded,
    error,
    sendResetCode,
    verifyResetCode,
    resetPassword,
    setActive
  };
}

/**
 * Hook for changing password when user is logged in
 * @returns {{ changePassword: Function, isLoading: boolean, error: any }}
 */
export function useChangePassword() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    setError(null);
    setIsLoading(true);
    try {
      // Check for bearer token in localStorage (for cross-origin auth)
      const bearerToken = typeof window !== 'undefined'
        ? localStorage.getItem('better_auth_token')
        : null;

      console.log('[Auth] changePassword - bearer token available:', !!bearerToken);

      // Use bearer token if available, otherwise rely on cookies
      const fetchOptions = bearerToken
        ? { headers: { Authorization: `Bearer ${bearerToken}` } }
        : {};

      console.log('[Auth] changePassword - calling API with fetchOptions');

      const result = await authClient.changePassword({
        currentPassword,
        newPassword,
        revokeOtherSessions: false, // Keep other sessions active
        fetchOptions // Pass fetch options inside the request object (same as getSession)
      }, {
        onError: (ctx) => {
          console.error('[Auth] changePassword error:', ctx.error);
        }
      });

      console.log('[Auth] changePassword result:', JSON.stringify(result, null, 2));

      if (result.error) {
        throw new Error(result.error.message || 'Failed to change password');
      }

      return { success: true };
    } catch (err) {
      console.error('[Auth] changePassword exception:', err);
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { changePassword, isLoading, error };
}
